import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { motion } from "framer-motion"
import { ArrowLeft, FileQuestion, Pencil, RotateCcw, Trash2 } from "lucide-react"
import { useCallback } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
	type AdminPost,
	deleteAdminPost,
	getAdminPost,
	getPostStats,
	isDraft,
	PLATFORMS,
	PostPreview,
	type PostStats as PostStatsData,
	type PreviewContent,
	placementOf,
	restoreAdminPost,
} from "@/entities/admin-post"
import { useSessionRole } from "@/entities/session"
import { PublicationPanel } from "@/features/publish-post"
import { formatAgo, formatWhen } from "@/shared/lib/format-date"
import { fadeUp, staggerContainer } from "@/shared/lib/motion"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { EmptyState } from "@/shared/ui/empty-state"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageTransition } from "@/shared/ui/page-transition"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { Skeleton } from "@/shared/ui/skeleton"
import { LoadSwap } from "@/shared/ui/load-swap"

function previewOf(post: AdminPost): PreviewContent {
	const attachments = (post.media ?? [])
		.filter((item) => !["preview", "video_thumb", "video_note_thumb"].includes(item.type))
		.sort((left, right) => left.order - right.order)
	return {
		title: post.title ?? "",
		shortDescription: post.shortDescription ?? "",
		description: post.description ?? "",
		coverUrl: post.media?.find((item) => item.type === "preview")?.url ?? null,
		formats: post.formats?.map((item) => item.name) ?? [],
		media: attachments.map((item) => ({
			key: item.id,
			kind: item.type === "photo" ? "photo" : item.type === "video" ? "video" : "file",
			imageUrl: item.type === "photo" ? item.url : (item.thumbnailUrl ?? null),
			name: item.filename,
		})),
	}
}

function summary(post: AdminPost): string {
	if (post.deletedAt) return "Удалён"
	if (isDraft(post)) return "Черновик"
	const live = PLATFORMS.filter((platform) => placementOf(post, platform).state === "live").length
	return live === PLATFORMS.length ? "Опубликован везде" : `Опубликован: ${live} из ${PLATFORMS.length}`
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
	return (
		<motion.div variants={fadeUp} className="min-h-[124px] rounded-2xl border border-border bg-card p-4">
			<p className="text-xs text-muted-foreground">{label}</p>
			<p className="mt-1.5 text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
			{hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
		</motion.div>
	)
}

const number = new Intl.NumberFormat("ru-RU")

/** Плитка статистики по высоте настоящей, чтобы смена на цифры не двигала страницу. */
const STAT_TILE = "h-[124px] rounded-2xl"

function StatsSkeleton() {
	return (
		<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
			{[0, 1, 2, 3].map((index) => (
				<Skeleton key={index} className={STAT_TILE} />
			))}
		</div>
	)
}

function PostStatsBlock({ post }: { post: AdminPost }) {
	const stats = useQuery({ queryKey: ["admin-post-stats", post.id], queryFn: () => getPostStats(post.id) })
	if (!stats.isLoading && !stats.data) return null
	return (
		// Один LoadSwap на обе ветки: скелетон гаснет поверх, цифры проявляются на его месте.
		<LoadSwap loading={stats.isLoading}>
			{stats.data ? <PostStats post={post} stats={stats.data} /> : <StatsSkeleton />}
		</LoadSwap>
	)
}

function PostStats({ post, stats }: { post: AdminPost; stats: PostStatsData }) {
	const { views, viewers, favorites, avgVideoProgress, lastViewedAt } = stats
	const videoDuration = post.media?.find((item) => item.type === "video")?.duration ?? null
	const watched =
		avgVideoProgress && videoDuration
			? `${Math.min(100, Math.round((avgVideoProgress / videoDuration) * 100))}%`
			: "нет данных"
	return (
		<motion.div
			variants={staggerContainer(0.04)}
			initial="hidden"
			animate="visible"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
		>
			<Stat label="Просмотры" value={number.format(views)} hint={lastViewedAt ? `последний ${formatAgo(lastViewedAt)}` : undefined} />
			<Stat label="Зрители" value={number.format(viewers)} hint={viewers ? `${(views / viewers).toFixed(1).replace(".", ",")} просмотра на человека` : undefined} />
			<Stat label="В избранном" value={number.format(favorites)} hint={viewers ? `${Math.round((favorites / viewers) * 100)}% зрителей` : undefined} />
			<Stat label="Досмотр видео" value={watched} hint={videoDuration ? "средняя позиция в видео" : undefined} />
		</motion.div>
	)
}

/**
 * Заглушка повторяет страницу: шапка с кнопками, разделы с заголовками, блоки публикации и
 * плитки статистики настоящей высоты, превью справа. Тогда при смене на содержимое ничего
 * не съезжает, а только проявляется на своём месте.
 */
function PostSkeleton() {
	return (
		<div aria-hidden="true">
			<header className="mb-8 flex flex-wrap items-start justify-between gap-4">
				<div className="min-w-0 flex-1">
					<div className="flex h-[17px] items-center">
						<Skeleton className="h-3 w-36" />
					</div>
					<div className="mt-1 flex h-9 items-center">
						<Skeleton className="h-7 w-2/3" />
					</div>
					<div className="mt-2 flex h-5 items-center">
						<Skeleton className="h-3.5 w-72 max-w-full" />
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Skeleton className="h-12 w-40 rounded-xl" />
					<Skeleton className="size-11 rounded-xl" />
				</div>
			</header>
			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
				<div className="min-w-0 space-y-8">
					<section>
						<div className="mb-3 flex h-7 items-center">
							<Skeleton className="h-5 w-28" />
						</div>
						<div className="space-y-3">
							{["h-[149px]", "h-[149px]", "h-[173px]"].map((height, index) => (
								<Skeleton key={index} className={`${height} rounded-2xl`} />
							))}
						</div>
					</section>
					<section>
						<div className="mb-3 flex h-7 items-center">
							<Skeleton className="h-5 w-32" />
						</div>
						<StatsSkeleton />
					</section>
				</div>
				<Skeleton className="h-[156px] rounded-2xl" />
			</div>
		</div>
	)
}

/**
 * Страница материала. Отсюда он публикуется на каждой площадке отдельно, здесь же видно,
 * как его читают. Редактирование содержимого вынесено в редактор.
 */
export function PostPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const { isAdmin } = useSessionRole()
	const { hash } = useLocation()
	// Из «Материалов без зрителей»: сразу к публикации.
	const scrollToPublish = useCallback(
		(node: HTMLElement | null) => {
			if (node && hash === "#publish") node.scrollIntoView({ behavior: "smooth", block: "start" })
		},
		[hash],
	)
	const query = useQuery({
		queryKey: ["admin-post", id],
		queryFn: () => getAdminPost(id!),
		enabled: !!id && isAdmin,
	})

	const refresh = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: ["admin-post", id] }),
			queryClient.invalidateQueries({ queryKey: ["admin-posts"] }),
		])

	const remove = useMutation({
		mutationFn: () => deleteAdminPost(id!),
		onSuccess: async () => {
			toast.success("Материал удалён", { description: "Его можно вернуть из раздела «Удалённые»" })
			await refresh()
			navigate("/admin")
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось удалить материал")),
	})
	const restore = useMutation({
		mutationFn: () => restoreAdminPost(id!),
		onSuccess: async () => {
			toast.success("Материал восстановлен")
			await refresh()
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось восстановить")),
	})

	if (!isAdmin) return <AccessDenied title="Материал" />

	const post = query.data

	return (
		<PageTransition>
			<div className="relative isolate">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_14%,transparent),transparent_70%)]"
				/>
				<div className="container mx-auto max-w-6xl px-3 py-5 sm:px-6 lg:py-8">
					<Link
						to="/admin"
						className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
					>
						<ArrowLeft className="size-4" />
						Контент
					</Link>

					<LoadSwap loading={query.isLoading}>
						{query.isLoading ? (
						<PostSkeleton />
					) : query.isError ? (
						<LoadErrorState onRetry={() => void query.refetch()} />
					) : !post ? (
						<EmptyState icon={FileQuestion} title="Материал не найден">
							Возможно, его удалили или ссылка неверная.
						</EmptyState>
					) : (
						<>
							<header className="mb-8 flex flex-wrap items-start justify-between gap-4">
								<div className="min-w-0 flex-1">
									<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
										{summary(post)}
									</p>
									<h1 className="mt-1 text-2xl font-bold tracking-tight break-words sm:text-3xl">
										{post.title || "Без названия"}
									</h1>
									<p className="mt-2 text-sm text-muted-foreground">
										Создан {formatWhen(post.createdAt)} · изменён {formatAgo(post.updatedAt)}
									</p>
								</div>
								<div className="flex items-center gap-2">
									{post.deletedAt ? (
										<Button isLoading={restore.isPending} onClick={() => restore.mutate()}>
											<RotateCcw />
											Восстановить
										</Button>
									) : (
										<>
											<Button asChild variant="secondary">
												<Link to={`/admin/edit/${post.id}`}>
													<Pencil />
													Редактировать
												</Link>
											</Button>
											<RowActionsMenu
												label="Ещё действия с материалом"
												actions={[
													{
														label: "Удалить",
														icon: Trash2,
														destructive: true,
														onSelect: () =>
															confirm({
																title: `Удалить «${post.title || "Без названия"}»?`,
																message:
																	"Материал пропадёт из приложений, а сообщение удалится из канала. Вернуть можно из раздела «Удалённые».",
																confirmText: "Удалить",
																onConfirm: () => remove.mutateAsync().then(() => undefined),
															}),
													},
												]}
											/>
										</>
									)}
								</div>
							</header>

							<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
								<div className="min-w-0 space-y-8">
									{post.deletedAt ? (
										<div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
											Материал удалён и нигде не показывается. Восстановите его, чтобы снова
											опубликовать.
										</div>
									) : (
										<section id="publish" ref={scrollToPublish} className="scroll-mt-6">
											<h2 className="mb-3 text-lg font-semibold tracking-tight">Публикация</h2>
											<PublicationPanel post={post} />
										</section>
									)}
									<section>
										<h2 className="mb-3 text-lg font-semibold tracking-tight">Как смотрят</h2>
										<PostStatsBlock post={post} />
									</section>
								</div>
								<aside className="space-y-4 lg:sticky lg:top-6">
									<PostPreview content={previewOf(post)} />
									{post.niches?.length || post.topics?.length ? (
										<section className="rounded-2xl border border-border bg-card p-4">
											<h2 className="mb-3 text-sm font-medium">Классификация</h2>
											<div className="flex flex-wrap gap-1.5">
												{[...(post.niches ?? []), ...(post.topics ?? []), ...(post.durations ?? [])].map(
													(item) => (
														<span
															key={item.id}
															className="rounded-full bg-secondary px-2.5 py-1 text-xs text-secondary-foreground"
														>
															{item.name}
														</span>
													),
												)}
											</div>
										</section>
									) : null}
								</aside>
							</div>
						</>
					)}
					</LoadSwap>
				</div>
			</div>
		</PageTransition>
	)
}
